"use client";

import { useEffect, useRef } from "react";
import { isAuthErrorLike, signOutAndGoToSignIn } from "@/lib/clientAuthError";
import {
  canAutoReload,
  isHydrationError,
  isReloadRecoverableError,
  performAutoReload,
} from "@/lib/recoverableError";

type ErrorPayload = {
  source: string;
  name?: string;
  message: string;
  stack?: string;
  digest?: string;
  code?: string;
  cause?: string;
  details?: string;
  path?: string;
  filename?: string;
  lineno?: number;
  colno?: number;
};

const toText = (value: unknown): string => {
  if (value instanceof Error) return value.stack || value.message;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const toLimitedText = (value: unknown, max = 1200): string => {
  const text = toText(value);
  return text.length > max ? `${text.slice(0, max)}...(truncated)` : text;
};

const extractErrorMeta = (value: unknown) => {
  if (!value || typeof value !== "object") {
    return {} as {
      name?: string;
      message?: string;
      stack?: string;
      digest?: string;
      code?: string;
      cause?: string;
      details?: string;
    };
  }

  const errLike = value as {
    name?: unknown;
    message?: unknown;
    stack?: unknown;
    digest?: unknown;
    code?: unknown;
    cause?: unknown;
    [key: string]: unknown;
  };

  const details: Record<string, unknown> = {};
  Object.keys(errLike).forEach((key) => {
    if (["name", "message", "stack", "digest", "code", "cause"].includes(key)) {
      return;
    }
    details[key] = errLike[key];
  });

  return {
    name: typeof errLike.name === "string" ? errLike.name : undefined,
    message: typeof errLike.message === "string" ? errLike.message : undefined,
    stack: typeof errLike.stack === "string" ? errLike.stack : undefined,
    digest: typeof errLike.digest === "string" ? errLike.digest : undefined,
    code:
      typeof errLike.code === "string" || typeof errLike.code === "number"
        ? String(errLike.code)
        : undefined,
    cause:
      typeof errLike.cause !== "undefined"
        ? toLimitedText(errLike.cause, 600)
        : undefined,
    details:
      Object.keys(details).length > 0
        ? toLimitedText(details, 1400)
        : undefined,
  };
};

// redirect() and notFound() signal by throwing, so they surface here as errors
// even though nothing failed. Reporting them pages us for ordinary navigation
// and for every access rule that hides a page.
const hasNextControlFlowMarker = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  const upper = value.toUpperCase();
  return upper.includes("NEXT_REDIRECT") || upper.includes("NEXT_NOT_FOUND");
};

const isExpectedNextControlFlow = (value: unknown): boolean => {
  if (hasNextControlFlowMarker(toText(value))) {
    return true;
  }

  if (!value || typeof value !== "object") {
    return false;
  }

  const errLike = value as {
    digest?: unknown;
    message?: unknown;
    stack?: unknown;
    cause?: unknown;
  };

  if (hasNextControlFlowMarker(errLike.digest)) {
    return true;
  }

  if (hasNextControlFlowMarker(errLike.message)) {
    return true;
  }

  if (hasNextControlFlowMarker(errLike.stack)) {
    return true;
  }

  if (
    typeof errLike.cause !== "undefined" &&
    isExpectedNextControlFlow(errLike.cause)
  ) {
    return true;
  }

  return false;
};

// Production React strips the server-vs-client diff out of hydration errors, so
// these reports arrive pointing at Next's own error handler and say nothing
// about the cause. Third-party DOM mutation before hydration is the usual
// culprit, so record the fingerprints it leaves instead.
const domFingerprint = (): string => {
  const html = document.documentElement;
  const marks: string[] = [];
  if (/translated-(ltr|rtl)/.test(html.className)) marks.push("google-translate");
  const fontTags = document.getElementsByTagName("font").length;
  if (fontTags) marks.push(`font-tags=${fontTags}`);
  const injected = Array.from(document.body?.attributes || [])
    .map((attr) => attr.name)
    .filter((name) => name.startsWith("data-gr") || name === "cz-shortcut-listen");
  if (injected.length) marks.push(`body-attrs=${injected.join(",")}`);
  if (html.lang && html.lang !== "en") marks.push(`lang=${html.lang}`);
  return marks.length ? marks.join(" ") : "none";
};

export default function GlobalErrorReporter() {
  const lastSentRef = useRef<Record<string, number>>({});

  useEffect(() => {
    const sendReport = async (payload: ErrorPayload) => {
      const key = `${payload.source}:${payload.name || ""}:${payload.message}`;
      const now = Date.now();
      const prev = lastSentRef.current[key] ?? 0;

      // Avoid flooding Telegram with repeated identical errors in quick loops.
      if (now - prev < 2000) return;
      lastSentRef.current[key] = now;

      const enriched = isHydrationError(payload.message)
        ? {
            ...payload,
            details: [payload.details, `dom: ${domFingerprint()}`]
              .filter(Boolean)
              .join(" | "),
          }
        : payload;

      try {
        await fetch("/api/report-error", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...enriched,
            path: window.location.pathname,
          }),
          keepalive: true,
        });
      } catch {
        // swallow reporter transport errors
      }
    };

    // Auto-reload window-level errors a fresh load fixes (stale chunk/deploy,
    // hydration, stale server-action ids). Shares the per-path guard with the
    // route boundaries so the two surfaces never double-reload.
    const tryRecoverableReload = (errorValue: unknown): boolean =>
      isReloadRecoverableError(errorValue) &&
      canAutoReload() &&
      performAutoReload();

    const onError = (event: ErrorEvent) => {
      const err = event.error as Error | undefined;
      const meta = extractErrorMeta(err);
      // Cross-origin scripts surface as an opaque "Script error." with no
      // stack/filename — nothing actionable, so drop it instead of flooding.
      if (
        !err &&
        !event.filename &&
        (event.message === "Script error." ||
          event.message === "Script error")
      ) {
        return;
      }
      if (isExpectedNextControlFlow(err || event.message)) {
        return;
      }
      if (tryRecoverableReload(err || event.message)) {
        return;
      }
      if (isAuthErrorLike(err || event.message)) {
        void signOutAndGoToSignIn();
      }
      void sendReport({
        source: "window.error",
        name: meta.name || err?.name || "WindowError",
        message:
          meta.message ||
          err?.message ||
          event.message ||
          "Unknown window error",
        stack: meta.stack || err?.stack,
        digest: meta.digest,
        code: meta.code,
        cause: meta.cause,
        details: meta.details,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason as unknown;
      const err = reason instanceof Error ? reason : undefined;
      const meta = extractErrorMeta(reason);
      if (isExpectedNextControlFlow(reason)) {
        return;
      }
      if (tryRecoverableReload(reason)) {
        return;
      }
      if (isAuthErrorLike(reason)) {
        void signOutAndGoToSignIn();
      }
      void sendReport({
        source: "window.unhandledrejection",
        name: meta.name || err?.name || "UnhandledRejection",
        message: meta.message || err?.message || toText(reason),
        stack: meta.stack || err?.stack,
        digest: meta.digest,
        code: meta.code,
        cause: meta.cause,
        details: meta.details,
      });
    };

    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      if (args.some((arg) => isExpectedNextControlFlow(arg))) {
        originalConsoleError(...args);
        return;
      }
      if (args.some((arg) => isAuthErrorLike(arg))) {
        void signOutAndGoToSignIn();
      }
      const firstErrorArg = args.find(
        (arg) =>
          arg instanceof Error ||
          (arg && typeof arg === "object" && "message" in (arg as object)),
      );
      const meta = extractErrorMeta(firstErrorArg);
      void sendReport({
        source: "console.error",
        name: meta.name || "ConsoleError",
        message: args.map(toText).join(" | "),
        stack: meta.stack,
        digest: meta.digest,
        code: meta.code,
        cause: meta.cause,
        details: meta.details,
      });
      originalConsoleError(...args);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      console.error = originalConsoleError;
    };
  }, []);

  return null;
}
